import { RiAddLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    AnchorHTMLAttributes,
    MutableRefObject,
    ReactElement,
    ReactNode,
} from "react";
import Button, { ButtonProps } from "../../button/Button";
import Styles from "../Table.module.scss";
import { ExtractParams, PathValues } from "../../../../contexts/Routing";
import RouterLink from "../../button/RouterLink";
import Link from "../../button/Link";

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement>;
export interface TableControlsButtonProps<TPath extends PathValues = PathValues> extends ButtonProps {
    text: string;
    link?: TPath;
    linkParameters?: ExtractParams<TPath>;
    href?: LinkProps["href"];
    target?: LinkProps["target"];
    rel?: LinkProps["rel"]
}

const TableControlsButton = React.forwardRef(<TPath extends PathValues = PathValues>(
    args: TableControlsButtonProps<TPath>,
    ref: React.ForwardedRef<HTMLElement>,
): ReactElement => {
    const {
        className,
        text,
        variant,
        size,
        link,
        linkParameters,
        href,
        prefixIcon,
        postfixIcon,
        isActive,
        isClose,
        target,
        rel,
        ...rest
    } = args;

    let actionDisplay: ReactNode | undefined;
    if (link) {
        actionDisplay = (
            <RouterLink
                className={classNames(Styles["action-button"], className)}
                variant={variant ?? "primary"}
                size={size ?? "small"}
                prefixIcon={prefixIcon}
                postfixIcon={postfixIcon}
                isActive={isActive}
                isClose={isClose}
                ref={ref as MutableRefObject<HTMLAnchorElement>}
                to={link}
                params={linkParameters}
            >
                {text}
            </RouterLink>
        );
    } else if (href) {
        actionDisplay = (
            <Link
                className={classNames(Styles["action-button"], className)}
                variant={variant ?? "primary"}
                size={size ?? "small"}
                prefixIcon={prefixIcon}
                postfixIcon={postfixIcon}
                isActive={isActive}
                isClose={isClose}
                href={href}
                target={target}
                rel={rel}
            >
                {text}
            </Link>
        );
    } else {
        actionDisplay = (
            <Button
                className={classNames(Styles["action-button"], className)}
                variant={variant ?? "primary"}
                size={size ?? "small"}
                prefixIcon={prefixIcon}
                postfixIcon={postfixIcon}
                isActive={isActive}
                isClose={isClose}
                ref={ref as MutableRefObject<HTMLButtonElement>}
                {...rest}
            >
                {text}
            </Button>
        );
    }

    return actionDisplay;
});

TableControlsButton.defaultProps = {

};

export type Type = <T extends PathValues = PathValues>(
    props: TableControlsButtonProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => ReactElement;

export default TableControlsButton as Type;
